import { FC, ReactNode } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

/**
 * Placeholder shape a column shows while loading. Declared per column so the
 * skeleton reads as the table it is standing in for, rather than as generic
 * grey lines — a badge column should shimmer as a pill, not as a sentence.
 */
export type DataTableSkeletonShape =
  | 'text'
  | 'title'
  | 'number'
  | 'badge'
  | 'bar'
  | 'action'
  | 'actionStack';

export interface DataTableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  /** Optional fixed column width as a CSS value (e.g. '15%'). */
  width?: string;
  /** Loading placeholder for this column's cells. Defaults to `'text'`. */
  skeleton?: DataTableSkeletonShape;
}

interface Props {
  columns: DataTableColumn[];
  children: ReactNode;
  minWidth?: number;
  isLoading?: boolean;
  /** Data failed to load (query error). Takes precedence over the empty state. */
  isError?: boolean;
  errorLabel?: string;
  isEmpty?: boolean;
  emptyLabel?: string;
  /**
   * Rich empty state rendered in place of `emptyLabel` — for a screen where the
   * empty case needs a heading and a way out, not just a sentence. Takes
   * precedence when set, and owns its own padding and alignment.
   */
  emptyContent?: ReactNode;
  /** Rendered below the table (e.g. a pagination bar). */
  footer?: ReactNode;
  /**
   * Rendered inside the footer, above `footer` — for a strip that belongs to the
   * table rather than to the pager, such as a legend for a column's encoding.
   *
   * Separate from `footer` so it survives on its own: a table with one page has
   * no pagination, and folding the two together would take the legend with it.
   */
  footerLead?: ReactNode;
  /** Fixed card height so the footer stays anchored (used for paginated tables). */
  height?: number;
  /** Placeholder rows while `isLoading`. Match the page size to avoid a jump. */
  skeletonRows?: number;
}

/**
 * One cell's placeholder. Widths vary slightly per row so the block reads as
 * content rather than as a grid of identical bars.
 */
const SkeletonCell: FC<{ shape: DataTableSkeletonShape; rowIndex: number }> = observer(
  ({ shape, rowIndex }) => {
    // Deterministic jitter — a random width would reshuffle on every render.
    const jitter = [0, 8, 4, 12, 6][rowIndex % 5];

    switch (shape) {
      case 'title':
        return <Skeleton variant="text" width={`${70 - jitter}%`} height={18} />;
      case 'number':
        return <Skeleton variant="text" width={28} height={16} />;
      case 'badge':
        return (
          <Skeleton variant="rounded" width={140 - jitter} height={24} sx={{ borderRadius: 999 }} />
        );
      case 'bar':
        return <Skeleton variant="rounded" width="90%" height={8} sx={{ borderRadius: 999 }} />;
      case 'action':
        return <Skeleton variant="rounded" width={150} height={32} sx={{ borderRadius: 2 }} />;
      case 'actionStack':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}>
            <Skeleton variant="rounded" width={150} height={32} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={150} height={32} sx={{ borderRadius: 2 }} />
          </Box>
        );
      case 'text':
      default:
        return <Skeleton variant="text" width={`${80 - jitter}%`} height={16} />;
    }
  },
);

/**
 * The shared data table: a bordered Paper with a sticky grey header of uppercase
 * caption labels, a scrollable body, and built-in loading / error / empty
 * states. Rows are supplied as `children` (`<TableRow>` elements).
 *
 * Started life as `AdminTable` inside the admin feature, but nothing about it
 * was admin-specific, so it lives here now and the admin console imports it
 * through a re-export. Reach for this before hand-rolling another
 * `TableContainer` — the loading/empty/error branches are the part that
 * otherwise gets re-implemented slightly differently every time.
 */
export const DataTable: FC<Props> = observer(
  ({
    columns,
    children,
    minWidth = 720,
    isLoading,
    isError,
    errorLabel,
    isEmpty,
    emptyLabel,
    emptyContent,
    footer,
    footerLead,
    height,
    skeletonRows = 8,
  }) => {
    const theme = useTheme();

    return (
      <Paper
        sx={{
          ...(height ? { height } : {}),
          minWidth: 0,
          borderRadius: 3,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.grey[300]}`,
          boxShadow: 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TableContainer sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto' }}>
          <Table sx={{ width: '100%', minWidth, tableLayout: 'fixed' }} stickyHeader>
            <colgroup>
              {columns.map((col) => (
                <col key={col.key} style={col.width ? { width: col.width } : undefined} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {columns.map((col, idx) => (
                  <TableCell
                    key={col.key}
                    align={col.align ?? 'left'}
                    sx={{
                      backgroundColor: theme.palette.grey[200],
                      py: 1.375,
                      pl: idx === 0 ? 2 : 1,
                      pr: idx === columns.length - 1 ? 2 : 1,
                      borderBottom: `1px solid ${theme.palette.grey[300]}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: theme.palette.text.tertiary,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {col.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                /* Row-shaped rather than a centred spinner: the column layout
                   is already known, so the table can hold its shape while the
                   data lands instead of collapsing and snapping back. */
                Array.from({ length: skeletonRows }).map((_, rowIdx) => (
                  <TableRow key={`skeleton-${rowIdx}`}>
                    {columns.map((col, idx) => (
                      <TableCell
                        key={col.key}
                        align={col.align ?? 'left'}
                        sx={{
                          py: 2,
                          pl: idx === 0 ? 2 : 1,
                          pr: idx === columns.length - 1 ? 2 : 1,
                        }}
                      >
                        <SkeletonCell shape={col.skeleton ?? 'text'} rowIndex={rowIdx} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    sx={{ border: 'none', py: 6, textAlign: 'center' }}
                  >
                    <Typography sx={{ fontSize: 13, color: theme.palette.error.main }}>
                      {errorLabel}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : isEmpty ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    sx={
                      emptyContent
                        ? { border: 'none', p: 0 }
                        : { border: 'none', py: 6, textAlign: 'center' }
                    }
                  >
                    {emptyContent ?? (
                      <Typography sx={{ fontSize: 13, color: theme.palette.text.tertiary }}>
                        {emptyLabel}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                children
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {(footer || footerLead) && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {footerLead}
            {footer}
          </Box>
        )}
      </Paper>
    );
  },
);
